/**
 * Header Component JavaScript
 * 
 * Handles mobile menu toggle, scroll behavior, and accessibility features.
 * 
 * @package EndrockTheme
 */

class HeaderMenu {
    header;
    mobileToggle;
    mobileMenu;
    mobileOverlay;
    body;
    isOpen = false;
    scrollThreshold = 50;
    lastScrollY = 0;

    constructor() {
        this.header = document.querySelector('.site-header');
        this.mobileToggle = document.querySelector('.js-mobile-menu-toggle');
        this.mobileMenu = document.querySelector('.mobile-menu');
        this.mobileOverlay = document.querySelector('.js-mobile-menu-overlay');
        this.body = document.body;

        this.init();
    }

    /**
     * Initialize all header functionality
     */
    init() {
        if (!this.header) return;

        // Mobile menu toggle
        this.initMobileMenu();

        // Scroll behavior
        this.initScrollBehavior();

        // Handle escape key
        this.initKeyboardHandlers();

        // Close menu on resize to desktop
        this.initResizeHandler();

        // Close menu when clicking links
        this.initMenuLinkHandlers();
    }

    /**
     * Initialize mobile menu toggle functionality
     */
    initMobileMenu() {
        if (!this.mobileToggle || !this.mobileMenu) return;

        this.mobileToggle.addEventListener('click', () => {
            this.toggleMobileMenu();
        });

        // Close menu when clicking overlay
        if (this.mobileOverlay) {
            this.mobileOverlay.addEventListener('click', () => {
                this.closeMobileMenu();
            });
        }
    }

    /**
     * Toggle mobile menu open/closed
     */
    toggleMobileMenu() {
        if (this.isOpen) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Open mobile menu
     */
    openMobileMenu() {
        if (!this.mobileMenu || !this.mobileToggle) return;

        this.isOpen = true;
        this.mobileMenu.classList.add('is-open');
        if (this.mobileOverlay) {
            this.mobileOverlay.classList.add('is-visible');
        }
        this.mobileToggle.classList.add('is-active');
        this.mobileToggle.setAttribute('aria-expanded', 'true');
        this.mobileMenu.setAttribute('aria-hidden', 'false');
        if (this.mobileOverlay) {
            this.mobileOverlay.setAttribute('aria-hidden', 'false');
        }
        this.body.classList.add('mobile-menu-open');

        // Trap focus inside menu
        this.trapFocus();

        // Announce to screen readers
        this.announceMenuState('Menu opened');
    }

    /**
     * Close mobile menu
     */
    closeMobileMenu() {
        if (!this.mobileMenu || !this.mobileToggle) return;

        this.isOpen = false;
        this.mobileMenu.classList.remove('is-open');
        if (this.mobileOverlay) {
            this.mobileOverlay.classList.remove('is-visible');
        }
        this.mobileToggle.classList.remove('is-active');
        this.mobileToggle.setAttribute('aria-expanded', 'false');
        this.mobileMenu.setAttribute('aria-hidden', 'true');
        if (this.mobileOverlay) {
            this.mobileOverlay.setAttribute('aria-hidden', 'true');
        }
        this.body.classList.remove('mobile-menu-open');

        // Return focus to toggle button
        this.mobileToggle.focus();

        // Announce to screen readers
        this.announceMenuState('Menu closed');
    }

    /**
     * Initialize scroll behavior (add/remove scrolled class)
     */
    initScrollBehavior() {
        window.addEventListener('scroll', () => {
            this.handleScroll();
        }, { passive: true });

        // Check on load
        this.handleScroll();
    }

    /**
     * Handle scroll events
     */
    handleScroll() {
        if (!this.header) return;

        const currentScrollY = window.scrollY;

        // Add scrolled class when past threshold
        if (currentScrollY > this.scrollThreshold) {
            this.header.classList.add('is-scrolled');
        } else {
            this.header.classList.remove('is-scrolled');
        }

        this.lastScrollY = currentScrollY;
    }

    /**
     * Initialize keyboard event handlers
     */
    initKeyboardHandlers() {
        document.addEventListener('keydown', (e) => {
            // Close menu on Escape key
            if (e.key === 'Escape' && this.isOpen) {
                this.closeMobileMenu();
            }
        });
    }

    /**
     * Initialize window resize handler
     */
    initResizeHandler() {
        let resizeTimer;

        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                // Close mobile menu if window is resized to desktop size
                if (window.innerWidth >= 992 && this.isOpen) {
                    this.closeMobileMenu();
                }
            }, 250);
        });
    }

    /**
     * Initialize menu link click handlers
     */
    initMenuLinkHandlers() {
        if (!this.mobileMenu) return;

        const menuLinks = this.mobileMenu.querySelectorAll('.mobile-menu__nav-link');
        const closeButton = this.mobileMenu.querySelector('.js-mobile-menu-close');
        
        menuLinks.forEach((link) => {
            link.addEventListener('click', () => {
                // Close menu when clicking internal links
                const href = link.getAttribute('href');
                if (href && href.startsWith('#')) {
                    this.closeMobileMenu();
                }
            });
        });

        if (closeButton) {
            closeButton.addEventListener('click', () => {
                this.closeMobileMenu();
            });
        }
    }

    /**
     * Trap focus inside mobile menu when open
     */
    trapFocus() {
        if (!this.mobileMenu) return;

        const focusableElements = this.mobileMenu.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        // Focus first element
        if (firstFocusable) {
            firstFocusable.focus();
        }

        // Handle tab key
        const handleTab = (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable?.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable?.focus();
                }
            }
        };

        document.addEventListener('keydown', handleTab);

        // Remove listener when menu closes
        const removeListener = () => {
            document.removeEventListener('keydown', handleTab);
        };

        // Store reference to remove later
        this.mobileMenu.addEventListener('transitionend', () => {
            if (!this.isOpen) {
                removeListener();
            }
        }, { once: true });
    }

    /**
     * Announce menu state to screen readers
     */
    announceMenuState(message) {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = message;

        document.body.appendChild(announcement);

        // Remove after announcement
        setTimeout(() => {
            announcement.remove();
        }, 1000);
    }
}

/**
 * Initialize header functionality
 */
const initHeader = () => {
    new HeaderMenu();
};
// Export for use in other modules if needed
export { HeaderMenu, initHeader };
